import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type EligibilityProcessType = 
  | 'eb1a' | 'eb2_niw' | 'eb3' | 'perm'
  | 'vawa' | 'u_visa' | 't_visa' 
  | 'sijs' | 'asylum' | 'tps' 
  | 'naturalization' | 'renewal' 
  | 'removal_defense' | 'other';

export interface DealEligibility {
  id: string;
  deal_id: string;
  created_at: string | null;
  updated_at: string | null;
  process_type: EligibilityProcessType;
  is_eligible: boolean | null;
  notes: string | null;
  evaluated_by: string | null;
}

export interface UpsertEligibilityInput {
  deal_id: string;
  process_type: EligibilityProcessType;
  is_eligible: boolean | null;
  notes?: string | null;
}

export function useDealEligibility(dealId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['deal-eligibility', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deal_eligibility')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as DealEligibility[];
    },
    enabled: !!dealId,
  });

  // Upsert mutation - creates or updates based on process_type
  const upsertMutation = useMutation({
    mutationFn: async (input: UpsertEligibilityInput) => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Check if record exists
      const { data: existing } = await supabase
        .from('deal_eligibility')
        .select('id')
        .eq('deal_id', input.deal_id)
        .eq('process_type', input.process_type)
        .single();

      if (existing) {
        // Update existing record
        const { data, error } = await supabase
          .from('deal_eligibility')
          .update({
            is_eligible: input.is_eligible,
            notes: input.notes,
            updated_at: new Date().toISOString(),
            evaluated_by: user?.id || null,
          })
          .eq('id', existing.id)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('deal_eligibility')
          .insert({
            deal_id: input.deal_id,
            process_type: input.process_type,
            is_eligible: input.is_eligible,
            notes: input.notes,
            evaluated_by: user?.id || null,
          })
          .select()
          .single();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-eligibility', dealId] });
    },
    onError: (error) => {
      console.error('Error upserting eligibility:', error);
      toast.error('Erro ao salvar elegibilidade');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('deal_eligibility')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-eligibility', dealId] });
      toast.success('Elegibilidade removida');
    },
    onError: (error) => {
      console.error('Error deleting eligibility:', error);
      toast.error('Erro ao remover elegibilidade');
    },
  });

  // Helper to get eligibility status for a specific process type
  const getEligibilityByType = (processType: EligibilityProcessType): DealEligibility | undefined => {
    return query.data?.find(e => e.process_type === processType);
  };

  return {
    eligibilities: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    upsertEligibility: upsertMutation.mutate,
    upsertEligibilityAsync: upsertMutation.mutateAsync,
    deleteEligibility: deleteMutation.mutate,
    getEligibilityByType,
    isUpserting: upsertMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
